package co.maytes.standupbot.dashboard.features.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Checkbox
import co.maytes.standupbot.dashboard.components.widgets.ControlSize
import co.maytes.standupbot.dashboard.components.widgets.CopyField
import co.maytes.standupbot.dashboard.components.widgets.DatePicker
import co.maytes.standupbot.dashboard.components.widgets.DayOfWeekPicker
import co.maytes.standupbot.dashboard.components.widgets.FormField
import co.maytes.standupbot.dashboard.components.widgets.RadioGroup
import co.maytes.standupbot.dashboard.components.widgets.RadioGroupVariant
import co.maytes.standupbot.dashboard.components.widgets.RadioItem
import co.maytes.standupbot.dashboard.components.widgets.Select
import co.maytes.standupbot.dashboard.components.widgets.SelectOption
import co.maytes.standupbot.dashboard.components.widgets.Switch
import co.maytes.standupbot.dashboard.components.widgets.Textarea
import co.maytes.standupbot.dashboard.components.widgets.TextInput
import co.maytes.standupbot.dashboard.components.widgets.TimePicker
import co.maytes.standupbot.dashboard.components.widgets.fieldControl
import co.maytes.standupbot.dashboard.components.widgets.fieldControlLook
import co.maytes.standupbot.dashboard.styles.Palette
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input

private val inputStates = listOf(
    GalleryState.Rest,
    GalleryState.Hover,
    GalleryState.Focus,
    GalleryState.Disabled,
    GalleryState.Loading,
    GalleryState.Invalid,
)
private val plainFieldStates = listOf(
    GalleryState.Rest,
    GalleryState.Hover,
    GalleryState.Focus,
    GalleryState.Disabled,
    GalleryState.Invalid,
)
private val pillStates = listOf(
    GalleryState.Rest,
    GalleryState.Hover,
    GalleryState.Focus,
    GalleryState.Active,
    GalleryState.Disabled,
    GalleryState.Invalid,
)
private val toggleStates = listOf(
    GalleryState.Rest,
    GalleryState.Hover,
    GalleryState.Focus,
    GalleryState.Active,
    GalleryState.Disabled,
    GalleryState.Loading,
)

private val cities = listOf(
    SelectOption("ams", "Amsterdam"),
    SelectOption("ber", "Berlin"),
    SelectOption("bru", "Brussels", disabled = true),
    SelectOption("cai", "Cairo"),
    SelectOption("cph", "Copenhagen"),
    SelectOption("dub", "Dublin"),
)

private val manyOptions = (1..200).map { SelectOption(it, "Workspace ${it.toString().padStart(3, '0')}") }

private const val AUTOSIZE_SAMPLE = "Shipped the Linear sync fix.\nStill reviewing the Slack retry PR.\nBlocked on staging access."
private const val DEMO_NOTE_LIMIT = 40

@Composable
fun FieldsSection() {
    GallerySection(
        id = "fields",
        title = "Form controls",
        description = "FormField wires label, hint, help and error to the control (for/id, aria-describedby, " +
            "aria-invalid, aria-required). Controls are 28, 32 or 40 px tall with the control radius. Hover darkens " +
            "the border, focus is the accent border plus the shared ring, invalid is the danger border plus an " +
            "icon, disabled is the subtle fill. Invalid and loading are driven by props, not forced attributes.",
    ) {
        FormFieldDemos()
        TextInputMatrix()
        TextareaMatrix()
        AutosizeDemo()
        SelectMatrix()
        SelectDemo200()
        CheckboxMatrix()
        RadioMatrix()
        SwitchMatrix()
        DatePickerMatrix()
        DayOfWeekMatrix()
        CopyFieldMatrix()
        FieldChecklists()
    }
}

@Composable
private fun FormFieldDemos() {
    Stack(10) {
        GalleryHeading("FormField")
        GalleryGrid(minColumn = 280) {
            GalleryPanel {
                GalleryNote("Label, hint and required marker. The control receives id, aria-required and aria-describedby.")
                FormField(label = "Workspace name", hint = "Shown in Slack", required = true) { field ->
                    Input(
                        type = InputType.Text,
                        attrs = Modifier.fieldControlLook().toAttrs {
                            fieldControl(field)
                            value("Acme")
                        },
                    )
                }
            }
            GalleryPanel {
                GalleryNote("Help is read with the control and is replaced by the error message while one shows.")
                TextInput(
                    value = "",
                    onValueChange = {},
                    label = "Slack channel",
                    help = "Pick the channel that receives the daily post.",
                    placeholder = "#eng-standup",
                )
            }
            GalleryPanel { ErrorFadeDemo() }
        }
    }
}

@Composable
private fun ErrorFadeDemo() {
    var name by remember { mutableStateOf("") }
    var showError by remember { mutableStateOf(false) }
    GalleryNote(
        "The error fades in over 120 ms with a 4 px rise. Its height is reserved, so the line below never moves.",
    )
    TextInput(
        value = name,
        onValueChange = { name = it },
        label = "Standup name",
        placeholder = "Platform",
        error = if (showError && name.isBlank()) "Give the standup a name" else null,
        reserveMessageSpace = true,
        required = true,
    )
    GalleryRow(gap = 8) {
        Button(
            label = "Validate",
            variant = ButtonVariant.Secondary,
            size = ButtonSize.Sm,
            onClick = { showError = true },
        )
        Button(
            label = "Reset",
            variant = ButtonVariant.Ghost,
            size = ButtonSize.Sm,
            onClick = {
                showError = false
                name = ""
            },
        )
    }
    GalleryMono("Content below the field", Palette.textTertiary)
}

@Composable
private fun TextInputMatrix() = GalleryMatrix(
    component = "TextInput: kind x size x state",
    variants = listOf("Empty", "Filled", "Numeric"),
    sizes = ControlSize.entries.map { it.name },
    states = inputStates,
    columnMin = 170,
) { variant, size, state ->
    var text by remember { mutableStateOf(if (variant == "Empty") "" else if (variant == "Numeric") "128" else "Acme") }
    Div(attrs = { style { property("width", "100%") } }) {
        TextInput(
            value = text,
            onValueChange = { text = it },
            placeholder = "Workspace name",
            error = if (state == GalleryState.Invalid) "Required" else null,
            size = ControlSize.valueOf(size),
            disabled = state == GalleryState.Disabled,
            loading = state == GalleryState.Loading,
            numeric = variant == "Numeric",
            ariaLabel = "$variant $size input",
        )
    }
}

@Composable
private fun TextareaMatrix() = GalleryMatrix(
    component = "Textarea: kind x state",
    variants = listOf("Fixed", "Autosize"),
    sizes = listOf(ControlSize.Md.name),
    states = plainFieldStates,
    columnMin = 210,
) { variant, _, state ->
    var text by remember { mutableStateOf(if (variant == "Autosize") AUTOSIZE_SAMPLE else "") }
    Div(attrs = { style { property("width", "100%") } }) {
        Textarea(
            value = text,
            onValueChange = { text = it },
            placeholder = "What did you ship?",
            error = if (state == GalleryState.Invalid) "Required" else null,
            disabled = state == GalleryState.Disabled,
            autosize = variant == "Autosize",
            minRows = 2,
            maxRows = 6,
            rows = 3,
            minHeightPx = 72,
            ariaLabel = "$variant textarea",
        )
    }
}

@Composable
private fun AutosizeDemo() {
    var text by remember { mutableStateOf(AUTOSIZE_SAMPLE) }
    GalleryPanel {
        GalleryHeading("Textarea autosize")
        GalleryNote("Grows with its lines from 2 rows up to 6, then scrolls. Type or paste to try it.")
        Textarea(
            value = text,
            onValueChange = { text = it },
            label = "Yesterday",
            autosize = true,
            minRows = 2,
            maxRows = 6,
        )
        GalleryMono("${text.lines().size} lines, ${text.length} characters")
    }
}

@Composable
private fun SelectMatrix() = GalleryMatrix(
    component = "Select: mode x size x state",
    variants = listOf("Native", "Popover", "Searchable"),
    sizes = ControlSize.entries.map { it.name },
    states = inputStates,
    note = "Native is the platform select, Popover is the styled listbox with type-ahead, Searchable adds a filter field.",
    columnMin = 170,
) { variant, size, state ->
    var selected by remember { mutableStateOf("ber") }
    Div(attrs = { style { property("width", "100%") } }) {
        Select(
            value = selected,
            options = cities,
            onValueChange = { selected = it },
            size = ControlSize.valueOf(size),
            disabled = state == GalleryState.Disabled,
            loading = state == GalleryState.Loading,
            error = if (state == GalleryState.Invalid) "Required" else null,
            searchable = variant == "Searchable",
            popover = variant != "Native",
            ariaLabel = "$variant $size select",
        )
    }
}

@Composable
private fun SelectDemo200() {
    var native by remember { mutableStateOf(7) }
    var popover by remember { mutableStateOf(42) }
    var searchable by remember { mutableStateOf(200) }
    GalleryPanel {
        GalleryHeading("Select with 200 options")
        GalleryNote(
            "Try Home and End, arrow keys, typing a few letters on the closed or open Popover select, and filtering " +
                "the Searchable one with a number such as 150. Enter selects, Esc closes and returns focus.",
        )
        GalleryGrid(minColumn = 240) {
            Select(
                value = native,
                options = manyOptions,
                onValueChange = { native = it },
                label = "Native",
                hint = "Platform control",
            )
            Select(
                value = popover,
                options = manyOptions,
                onValueChange = { popover = it },
                label = "Popover listbox",
                hint = "Type-ahead",
                popover = true,
            )
            Select(
                value = searchable,
                options = manyOptions,
                onValueChange = { searchable = it },
                label = "Searchable",
                hint = "Filter",
                searchable = true,
            )
        }
        GalleryMono("native=$native popover=$popover searchable=$searchable")
    }
}

@Composable
private fun CheckboxMatrix() = GalleryMatrix(
    component = "Checkbox: value x size x state",
    variants = listOf("Unchecked", "Checked"),
    sizes = ControlSize.entries.map { it.name },
    states = plainFieldStates,
    columnMin = 150,
) { variant, size, state ->
    var checked by remember { mutableStateOf(variant == "Checked") }
    Checkbox(
        checked = checked,
        onCheckedChange = { checked = it },
        label = "Label",
        size = ControlSize.valueOf(size),
        disabled = state == GalleryState.Disabled,
        invalid = state == GalleryState.Invalid,
    )
}

@Composable
private fun RadioMatrix() = GalleryMatrix(
    component = "Radio: layout x state",
    variants = RadioGroupVariant.entries.map { it.name },
    sizes = listOf(ControlSize.Md.name),
    states = plainFieldStates,
    columnMin = 170,
) { variant, _, state ->
    var selected by remember { mutableStateOf("a") }
    RadioGroup(
        value = selected,
        items = listOf(
            RadioItem("a", "Option A", description = if (variant == "Card") "First choice" else null),
            RadioItem("b", "Option B", description = if (variant == "Card") "Second choice" else null),
        ),
        onValueChange = { selected = it },
        disabled = state == GalleryState.Disabled,
        error = if (state == GalleryState.Invalid) "Pick one" else null,
        variant = RadioGroupVariant.valueOf(variant),
        label = "Cadence",
    )
}

@Composable
private fun SwitchMatrix() = GalleryMatrix(
    component = "Switch: value x size x state",
    variants = listOf("Off", "On"),
    sizes = ControlSize.entries.map { it.name },
    states = toggleStates,
    columnMin = 150,
) { variant, size, state ->
    var checked by remember { mutableStateOf(variant == "On") }
    Switch(
        checked = checked,
        onCheckedChange = { checked = it },
        label = "Label",
        size = ControlSize.valueOf(size),
        disabled = state == GalleryState.Disabled,
        loading = state == GalleryState.Loading,
        ariaLabel = "$variant $size switch",
    )
}

@Composable
private fun DatePickerMatrix() = GalleryMatrix(
    component = "DatePicker and TimePicker: kind x size x state",
    variants = listOf("Date", "Time"),
    sizes = ControlSize.entries.map { it.name },
    states = plainFieldStates,
    columnMin = 170,
) { variant, size, state ->
    var text by remember { mutableStateOf(if (variant == "Date") "2026-10-13" else "09:30") }
    Div(attrs = { style { property("width", "100%") } }) {
        if (variant == "Date") {
            DatePicker(
                value = text,
                onChange = { text = it },
                size = ControlSize.valueOf(size),
                disabled = state == GalleryState.Disabled,
                error = if (state == GalleryState.Invalid) "Pick a date" else null,
                ariaLabel = "$size date",
            )
        } else {
            TimePicker(
                value = text,
                onChange = { text = it },
                size = ControlSize.valueOf(size),
                disabled = state == GalleryState.Disabled,
                error = if (state == GalleryState.Invalid) "Pick a time" else null,
                ariaLabel = "$size time",
            )
        }
    }
}

@Composable
private fun DayOfWeekMatrix() = GalleryMatrix(
    component = "DayOfWeekPicker: selection x size x state",
    variants = listOf("None", "Weekdays"),
    sizes = ControlSize.entries.map { it.name },
    states = pillStates,
    columnMin = 390,
) { variant, size, state ->
    var days by remember { mutableStateOf<Set<Int>>(if (variant == "Weekdays") setOf(1, 2, 3, 4, 5) else emptySet()) }
    DayOfWeekPicker(
        selected = days,
        onChange = { days = it },
        size = ControlSize.valueOf(size),
        disabled = state == GalleryState.Disabled,
        invalid = state == GalleryState.Invalid,
    )
}

@Composable
private fun CopyFieldMatrix() = GalleryMatrix(
    component = "CopyField: size x state",
    variants = listOf("Default"),
    sizes = listOf(ControlSize.Md.name, ControlSize.Lg.name),
    states = listOf(GalleryState.Rest, GalleryState.Hover, GalleryState.Focus),
    note = "Read-only monospace value. The copy button swaps its icon for a check over 150 ms and reports Copied for 1.2 s.",
    columnMin = 280,
) { _, size, _ ->
    Div(attrs = { style { property("width", "100%") } }) {
        CopyField(
            value = "https://standupbot.example/invite/9f3c1e2a7b",
            size = ControlSize.valueOf(size),
            ariaLabel = "$size invite link",
        )
    }
}

@Composable
private fun FieldChecklists() {
    GalleryGrid(minColumn = 380) {
        KeyboardChecklist(
            "FormField",
            listOf(
                keys("Focus lands on the control; the label is a real label and is not a tab stop", "Tab"),
                keys("Click the label to focus the control", "Click"),
            ),
        )
        KeyboardChecklist(
            "TextInput",
            listOf(
                keys("Move in and out", "Tab", "Shift+Tab"),
                keys("Clear is a mouse affordance: select all and delete clears from the keyboard", "Ctrl+A", "Delete"),
            ),
        )
        KeyboardChecklist(
            "Textarea",
            listOf(
                keys("Move in and out", "Tab", "Shift+Tab"),
                keys("New line", "Enter"),
                keys("Autosize grows with the lines up to the maximum, then scrolls", "Enter"),
            ),
        )
        KeyboardChecklist(
            "Select (native)",
            listOf(
                keys("Open the platform list", "Space", "Alt+Down"),
                keys("Change the value", "Up", "Down"),
                keys("Jump by typing", "a-z"),
            ),
        )
        KeyboardChecklist(
            "Select (popover and searchable)",
            listOf(
                keys("Open the list from the trigger", "Enter", "Space", "Down", "Up"),
                keys("Move the highlighted option, skipping disabled ones", "Down", "Up"),
                keys("First and last option", "Home", "End"),
                keys("Popover: jump by typing; repeat a letter to cycle (resets after 700 ms)", "a-z"),
                keys("Searchable: type to filter, prefix matches first", "a-z"),
                keys("Choose the highlighted option", "Enter"),
                keys("Close and return focus to the trigger", "Esc"),
                keys("Close and move on", "Tab"),
            ),
        )
        KeyboardChecklist(
            "Checkbox",
            listOf(
                keys("Move focus", "Tab"),
                keys("Toggle", "Space"),
            ),
        )
        KeyboardChecklist(
            "Radio group",
            listOf(
                keys("Enter the group on the checked option", "Tab"),
                keys("Move and select the next or previous option", "Down", "Right", "Up", "Left"),
            ),
        )
        KeyboardChecklist(
            "Switch",
            listOf(
                keys("Move focus", "Tab"),
                keys("Toggle", "Space"),
                keys("Toggle", "Enter"),
            ),
        )
        KeyboardChecklist(
            "DatePicker and TimePicker",
            listOf(
                keys("Move between the date or time segments", "Tab", "Left", "Right"),
                keys("Step a segment", "Up", "Down"),
                keys("Open the browser picker", "Alt+Down"),
            ),
        )
        KeyboardChecklist(
            "DayOfWeekPicker",
            listOf(
                keys("Move between days", "Tab"),
                keys("Toggle the focused day (aria-pressed)", "Space", "Enter"),
            ),
        )
        KeyboardChecklist(
            "CopyField",
            listOf(
                keys("Focus the value to select and copy it by hand", "Tab"),
                keys("Activate the copy button", "Enter", "Space"),
            ),
        )
    }
}
